<script>
import { getInitials, formatNumber } from '@utils/Format'
import { agruparFontes } from '@modules/aawz-intelligence-v2/utils/fontes'

// Único precedente de delay para `v-menu` no repo
// (`icon-no-module-access.vue:32`). Só no FECHAR: abrir é imediato, senão o
// gesto de passar o mouse não parece responder.
const ATRASO_PARA_FECHAR = 125

// Respiro entre o chip e o popover. Com o `offset-y` puro eles ficam colados e
// o popover lê como continuação do chip, não como camada por cima dele.
//
// ⚠️ Empurra para CIMA, porque o menu abre para cima (ver o `top` no template).
const FOLGA_ACIMA_DO_CHIP = 6

const CASAS_DECIMAIS = 0

export default {
  name: 'IntelligenceV2ChatMessageFontes',
  props: {
    // A lista CRUA como o backend manda: `[{ tabela, registros }]`. A
    // consolidação é daqui — ver `utils/fontes.js`.
    sources: {
      type: Array,
      default: function () {
        return []
      },
    },
  },
  data() {
    return {
      ATRASO_PARA_FECHAR,
      FOLGA_ACIMA_DO_CHIP,
      // O `open-on-hover` da Vuetify NÃO liga o clique no ativador, então sem
      // este modelo o chip não abriria por teclado.
      aberto: false,
    }
  },
  computed: {
    fontes() {
      return agruparFontes(this.sources).map(function (fonte) {
        return {
          ...fonte,
          inicial: getInitials(fonte.rotulo),
          registrosFormatados: formatNumber(fonte.registros, CASAS_DECIMAIS),
        }
      })
    },
    temFontes() {
      return this.fontes.length > 0
    },
    // A de maior volume representa o turno no chip fechado.
    principal() {
      return this.fontes[0] || null
    },
    demais() {
      return this.fontes.length - 1
    },
    rotuloDoGatilho() {
      const total = this.fontes.length
      const sufixo = total === 1 ? '1 fonte' : total + ' fontes'
      return 'Ver as fontes desta resposta: ' + sufixo
    },
  },
}
</script>

<template>
  <div v-if="temFontes" class="chat-message-fontes">
    <span class="chat-message-fontes__label ia-texto-selo">Fontes</span>

    <v-menu
      v-model="aberto"
      open-on-hover
      offset-y
      :close-on-content-click="false"
      :close-delay="ATRASO_PARA_FECHAR"
      top
      :nudge-top="FOLGA_ACIMA_DO_CHIP"
      content-class="ia-fontes-menu"
      min-width="260"
      max-width="340"
    >
      <template v-slot:activator="{ on, attrs }">
        <button
          type="button"
          class="chat-message-fontes__chip ia-texto-auxiliar"
          :aria-label="rotuloDoGatilho"
          v-bind="attrs"
          v-on="on"
          @click="aberto = !aberto"
        >
          <span class="chat-message-fontes__avatar ia-texto-micro" aria-hidden="true">
            {{ principal.inicial }}
          </span>
          <span class="chat-message-fontes__nome">{{ principal.rotulo }}</span>
          <span class="chat-message-fontes__count">
            {{ principal.registrosFormatados }}
          </span>
          <span v-if="demais" class="chat-message-fontes__mais">
            +{{ demais }}
          </span>
        </button>
      </template>

      <!-- Declarado NESTE template, então carrega o `data-v` do scope e o CSS
           scoped o alcança mesmo com o `v-menu` teleportando o conteúdo para
           fora do componente. Mesmo padrão de `clientes-recentes-v2.vue:46`.
           O que o scoped não alcança é o `.v-menu__content`, que a Vuetify
           cria — por isso o `content-class` e a partial global. -->
      <div class="chat-message-fontes__painel">
        <div class="chat-message-fontes__painel-topo ia-texto-selo">Fontes</div>
        <ul class="chat-message-fontes__lista">
          <li
            v-for="fonte in fontes"
            :key="fonte.tabela"
            class="chat-message-fontes__item"
          >
            <span class="chat-message-fontes__avatar ia-texto-micro" aria-hidden="true">
              {{ fonte.inicial }}
            </span>
            <!-- O nome técnico fica no `title`: é o que serve para depurar de
                 onde o número saiu, e só personas internas veem este bloco. -->
            <span class="chat-message-fontes__item-nome" :title="fonte.tabela">
              {{ fonte.rotulo }}
            </span>
            <span class="chat-message-fontes__item-count">
              {{ fonte.registrosFormatados }}
            </span>
          </li>
        </ul>
      </div>
    </v-menu>
  </div>
</template>

<style lang="sass" scoped>
.chat-message-fontes
  display: flex
  align-items: center
  gap: 6px

  // O `v-menu` embrulha o ativador num `div.v-menu` que mede 0×0 mas ainda é
  // item de flex real: sem isto ele soma o `gap` dos DOIS lados e o chip nasce
  // com o dobro do respiro do rótulo. Mesma armadilha e mesmo remédio de
  // `curadoria/suggestion-card.vue:258`.
  ::v-deep .v-menu
    display: contents

.chat-message-fontes__label
  color: var(--neutral-400)
  text-transform: uppercase
  letter-spacing: 0.03em

// Molde do `suggestion-chips`: `<button>` (foco por teclado de graça) sobre a
// superfície recessada da família quente.
.chat-message-fontes__chip
  color: var(--neutral-900)
  display: inline-flex
  align-items: center
  gap: 6px
  border: 0
  border-radius: 999px
  padding: 3px 10px 3px 4px
  background: var(--ia-recessed-bg)
  cursor: pointer
  transition: background 0.15s ease

  &:hover
    background: var(--sidebar-warm-hover)

.chat-message-fontes__avatar
  color: var(--neutral-700)
  display: flex
  align-items: center
  justify-content: center
  flex-shrink: 0
  width: 20px
  height: 20px
  border-radius: 50%
  background: var(--sidebar-warm-border)

.chat-message-fontes__count
  color: var(--neutral-400)

.chat-message-fontes__mais
  padding-left: 2px
  color: var(--neutral-700)
  font-weight: 600

// Cabeçalho quente + corpo claro: o mesmo par do popup e do painel lateral
// (`_ia-painel-lateral.sass:101` e `:197`).
.chat-message-fontes__painel-topo
  color: var(--neutral-700)
  padding: 8px 12px
  background: var(--sidebar-warm-bg)
  border-bottom: 1px solid var(--sidebar-warm-border)
  text-transform: uppercase
  letter-spacing: 0.03em

.chat-message-fontes__lista
  // Reset do `<ul>`: sem isto vem o recuo e o marcador do user-agent.
  list-style: none
  margin: 0
  padding: 4px 0
  max-height: 280px
  overflow-y: auto
  background: var(--ia-content-bg)

.chat-message-fontes__item
  display: flex
  align-items: center
  gap: 8px
  padding: 6px 12px
  font-size: var(--ia-font-auxiliar)

.chat-message-fontes__item-nome
  flex: 1
  min-width: 0
  color: var(--neutral-900)
  // Rótulo longo trunca em vez de esticar o menu ou quebrar em duas linhas.
  overflow: hidden
  text-overflow: ellipsis
  white-space: nowrap

.chat-message-fontes__item-count
  flex-shrink: 0
  color: var(--neutral-700)
  font-variant-numeric: tabular-nums
</style>
